/**
 * Team Admin → Fanatics (AC-FANATIC-009, AC-FANATIC-010, AC-FANATIC-011).
 *
 * Three jobs on one screen:
 *
 *  1. **Access mode.** `PATCH /tenants/v1/{id}` with `fanatic_access_mode`.
 *     Takes effect immediately for *new* follows; grants that already exist are
 *     untouched, and the copy says so.
 *  2. **Follower list + revoke.** `GET /tenants/v1/{id}/fanatic-grants` does the
 *     search, status filter, sort and paging server-side, so query state lives
 *     in the URL and survives a refresh. Revoking is **irreversible** — the
 *     confirm dialog states that, and takes an optional free-form reason that
 *     lands in the audit trail.
 *  3. **Invites.** `POST /tenants/v1/{id}/fanatic-invites`. There is no
 *     notification service yet, so the response carries the one-time link for
 *     the admin to copy; the database only ever stores its digest.
 *
 * Fanatics never consume a paid staff seat, so nothing here touches seat usage.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { useToast } from '../../app/toast-context';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { DataTable, type DataTableColumn } from '../../components/data-table';
import { DeskSection } from '../../components/desk-section';
import { OverlayDialog } from '../../components/overlay-dialog';
import { PageHeader } from '../../components/page-header';
import { StatusPill, type StatusTone } from '../../components/status-pill';
import { TextField } from '../../components/text-field';
import { ApiError } from '../../lib/api-error';
import type {
  FanaticAccessMode,
  FanaticGrantStatusFilter,
  TeamFanaticGrant,
  TeamFanaticGrantSortField,
} from '../../lib/api-types';
import { DEFAULT_PAGE_SIZE } from '../../lib/constants';
import { TableExportButton } from '../integrations/export-dialog';

import { buildFanaticsExportSource } from './team-admin-export';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u;
const MAX_REASON_LENGTH = 280;

const ACCESS_MODES: ReadonlyArray<{
  value: FanaticAccessMode;
  title: string;
  hint: string;
}> = [
  {
    value: 'public-follow',
    title: 'Public follow (default)',
    hint: 'Anyone with an account can follow your team and see scores, team totals and fan-visible video.',
  },
  {
    value: 'invite-only',
    title: 'Invite only',
    hint: 'Only people you invite by email can view your team. Self-service follows are refused.',
  },
];

const STATUS_FILTERS: ReadonlyArray<{ value: FanaticGrantStatusFilter; label: string }> = [
  { value: 'active', label: 'Active followers' },
  { value: 'pending_invite', label: 'Pending invites' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'all', label: 'All' },
];

const STATUS_TONE: Record<TeamFanaticGrant['status'], StatusTone> = {
  active: 'success',
  pending_invite: 'warning',
  revoked: 'neutral',
};

const STATUS_LABEL: Record<TeamFanaticGrant['status'], string> = {
  active: 'Active',
  pending_invite: 'Invite pending',
  revoked: 'Revoked',
};

const PARAM = { query: 'q', status: 'status', sort: 'sort', dir: 'dir', page: 'page' } as const;

function toMessage(caught: unknown, fallback: string): string {
  return ApiError.isApiError(caught) && !caught.isOffline ? caught.message : fallback;
}

function grantSource(grant: TeamFanaticGrant): string {
  if (grant.status === 'pending_invite') return 'Invited by team admin';
  if (grant.granted_by === null) return 'Followed themselves';
  return 'Granted by team admin';
}

function formatMoment(iso: string | null): string {
  if (iso === null) return '—';
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
}

function readStatus(raw: string | null): FanaticGrantStatusFilter {
  return STATUS_FILTERS.some((option) => option.value === raw)
    ? (raw as FanaticGrantStatusFilter)
    : 'active';
}

function readSort(raw: string | null): TeamFanaticGrantSortField {
  const allowed: TeamFanaticGrantSortField[] = [
    'fanatic_email',
    'granted_at',
    'status',
    'updated_at',
  ];
  return allowed.includes(raw as TeamFanaticGrantSortField)
    ? (raw as TeamFanaticGrantSortField)
    : 'granted_at';
}

function readPositive(raw: string | null, fallback: number): number {
  const parsed = Number.parseInt(raw ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export function FanaticManagementPage(): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const { session } = useSession();
  const tenantId = session?.tenantId ?? null;
  const [searchParams, setSearchParams] = useSearchParams();

  const query = searchParams.get(PARAM.query) ?? '';
  const status = readStatus(searchParams.get(PARAM.status));
  const sort = readSort(searchParams.get(PARAM.sort));
  const direction = searchParams.get(PARAM.dir) === 'asc' ? 'asc' : 'desc';
  const page = readPositive(searchParams.get(PARAM.page), 1);

  const [grants, setGrants] = useState<TeamFanaticGrant[]>([]);
  const [total, setTotal] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [accessMode, setAccessMode] = useState<FanaticAccessMode | null>(null);
  const [modeError, setModeError] = useState<string | null>(null);
  const [isModeBusy, setIsModeBusy] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<TeamFanaticGrant | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const patchParams = useCallback(
    (changes: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(changes)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (tenantId === null) return undefined;
    let isCancelled = false;
    void (async () => {
      try {
        const tenant = await apiClient.getTenant(tenantId);
        if (!isCancelled) setAccessMode(tenant.fanatic_access_mode ?? 'public-follow');
      } catch (caught) {
        if (!isCancelled) {
          setModeError(toMessage(caught, 'We could not read your current fanatic access mode.'));
        }
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [apiClient, tenantId]);

  useEffect(() => {
    if (tenantId === null) return undefined;
    const controller = new AbortController();
    setIsLoading(true);
    void (async () => {
      try {
        const result = await apiClient.listTeamFanaticGrants(
          tenantId,
          {
            q: query === '' ? undefined : query,
            status,
            sort,
            direction,
            page,
            page_size: DEFAULT_PAGE_SIZE,
          },
          controller.signal
        );
        setGrants(result.grants);
        setTotal(result.pagination.total);
        setActiveCount(result.active_count);
        setError(null);
      } catch (caught) {
        if (controller.signal.aborted) return;
        setError(toMessage(caught, 'We could not load your team’s fanatics.'));
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();
    return () => controller.abort();
  }, [apiClient, direction, page, query, reloadToken, sort, status, tenantId]);

  const changeMode = async (next: FanaticAccessMode): Promise<void> => {
    if (tenantId === null || next === accessMode) return;
    setIsModeBusy(true);
    setModeError(null);
    const previous = accessMode;
    setAccessMode(next);
    try {
      const tenant = await apiClient.updateTenant(tenantId, { fanatic_access_mode: next });
      setAccessMode(tenant.fanatic_access_mode ?? next);
      showToast(
        next === 'invite-only'
          ? 'Only people you invite can follow this team now.'
          : 'Anyone can follow this team now.',
        'success'
      );
    } catch (caught) {
      setAccessMode(previous);
      setModeError(toMessage(caught, 'We could not change the access mode. Please try again.'));
    } finally {
      setIsModeBusy(false);
    }
  };

  const revoke = async (grant: TeamFanaticGrant): Promise<void> => {
    if (tenantId === null) return;
    setRevokeError(null);
    try {
      await apiClient.revokeFanaticGrant(
        tenantId,
        grant.grant_id,
        revokeReason.trim() === '' ? null : revokeReason.trim()
      );
      showToast(
        `${grant.fanatic_email ?? 'That account'} can no longer view this team.`,
        'success'
      );
      setPendingRevoke(null);
      setRevokeReason('');
      reload();
    } catch (caught) {
      setRevokeError(toMessage(caught, 'We could not revoke that access. Please try again.'));
    }
  };

  const columns = useMemo<DataTableColumn<TeamFanaticGrant>[]>(
    () => [
      {
        id: 'fanatic_email',
        header: 'Fanatic',
        isSortable: true,
        isPinned: true,
        minWidth: 220,
        maxWidth: 420,
        grow: 3,
        render: (grant) => {
          const email = grant.fanatic_email ?? grant.invite_email ?? 'Unknown account';
          return (
            <span className="fan-cell" title={email}>
              <span className="fan-cell__email">{email}</span>
              {grant.fanatic_display_name === null ? null : (
                <span className="fan-cell__name">{grant.fanatic_display_name}</span>
              )}
            </span>
          );
        },
        renderDetail: (grant) => grant.fanatic_email ?? grant.invite_email ?? 'Unknown account',
      },
      {
        id: 'status',
        header: 'Status',
        isSortable: true,
        priority: 1,
        minWidth: 130,
        maxWidth: 150,
        grow: 0,
        render: (grant) => (
          <StatusPill tone={STATUS_TONE[grant.status]}>{STATUS_LABEL[grant.status]}</StatusPill>
        ),
      },
      {
        id: 'granted_at',
        header: 'Granted',
        isSortable: true,
        priority: 2,
        minWidth: 200,
        maxWidth: 220,
        grow: 0,
        render: (grant) => formatMoment(grant.granted_at ?? grant.created_at),
      },
      {
        id: 'granted_by',
        header: 'By',
        priority: 3,
        minWidth: 170,
        grow: 1,
        render: (grant) =>
          grant.status === 'revoked'
            ? `Revoked ${formatMoment(grant.revoked_at)}`
            : grantSource(grant),
      },
    ],
    []
  );

  if (tenantId === null) {
    return (
      <section className="fan-panel">
        <h1 className="fan-panel__title">Fanatics</h1>
        <p className="banner banner--error" role="alert">
          We could not work out which team you are administering. Please sign in again.
        </p>
      </section>
    );
  }

  return (
    <section className="stack fan-admin">
      <PageHeader
        title="Fanatics"
        context={
          <>
            {activeCount} {activeCount === 1 ? 'person follows' : 'people follow'} this team · fans
            see scores, team totals and fan-visible video only, and never use a staff seat.
          </>
        }
        actions={
          <>
            <TableExportButton
              isDisabled={total === 0}
              source={() =>
                buildFanaticsExportSource(apiClient, tenantId, {
                  q: query,
                  status,
                  sort,
                  direction,
                })
              }
            />
            <button
              className="btn btn--primary"
              onClick={() => setIsInviteOpen(true)}
              type="button"
            >
              Invite Fanatic
            </button>
          </>
        }
      />

      <DeskSection
        className="fan-access"
        title="Who can follow this team"
        description="Changing this applies to new follows only; people who already follow you keep access."
      >
        {modeError !== null ? (
          <p className="banner banner--error" role="alert">
            {modeError}
          </p>
        ) : null}
        <fieldset className="fan-radio-group fan-radio-group--row">
          <legend className="visually-hidden">Fanatic access mode</legend>
          {ACCESS_MODES.map((option) => (
            <label
              className={`fan-radio${accessMode === option.value ? ' fan-radio--selected' : ''}`}
              key={option.value}
            >
              <input
                checked={accessMode === option.value}
                disabled={isModeBusy || accessMode === null}
                name="fanatic-access-mode"
                onChange={() => void changeMode(option.value)}
                type="radio"
                value={option.value}
              />
              <span className="fan-radio__text">
                <span className="fan-radio__title">{option.title}</span>
                <span className="fan-radio__hint">{option.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
      </DeskSection>

      <DataTable<TeamFanaticGrant>
        caption="Fanatics with access to this team"
        surface="fanatics"
        layoutName="Fanatics columns"
        columns={columns}
        emptyDescription="Invite someone by email, or switch to Public Follow so fans can follow you themselves."
        emptyTitle="No fanatics yet"
        emptyAction={
          <button
            className="btn btn--primary btn--sm"
            onClick={() => setIsInviteOpen(true)}
            type="button"
          >
            Invite a Fanatic
          </button>
        }
        error={error}
        filters={[
          {
            id: 'status',
            label: 'Status',
            value: status,
            defaultValue: 'active',
            options: STATUS_FILTERS,
            onChange: (value) => patchParams({ [PARAM.status]: value, [PARAM.page]: null }),
          },
        ]}
        getRowId={(grant) => grant.grant_id}
        getRowClassName={(grant) =>
          grant.status === 'revoked' ? 'data-table__row--inactive' : undefined
        }
        isLoading={isLoading}
        onRetry={reload}
        onSortChange={(next) =>
          patchParams({ [PARAM.sort]: next.by, [PARAM.dir]: next.order, [PARAM.page]: null })
        }
        pagination={{
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          total,
          onPageChange: (next) => patchParams({ [PARAM.page]: String(next) }),
        }}
        actionsWidth={48}
        rowMenu={(grant) =>
          grant.status === 'revoked'
            ? []
            : [
                {
                  id: 'revoke',
                  label: 'Revoke',
                  tone: 'danger',
                  onSelect: () => {
                    setPendingRevoke(grant);
                    setRevokeReason('');
                    setRevokeError(null);
                  },
                },
              ]
        }
        getRowMenuLabel={(grant) =>
          `More actions for ${grant.fanatic_email ?? grant.invite_email ?? 'this account'}`
        }
        rows={grants}
        search={{
          value: query,
          onChange: (value) => patchParams({ [PARAM.query]: value, [PARAM.page]: null }),
          label: 'Search by email',
          placeholder: 'Search by email',
        }}
        sort={{ by: sort, order: direction }}
      />

      <GrantAuditLog grants={grants} />

      {isInviteOpen ? (
        <InviteFanaticDialog
          onClose={() => setIsInviteOpen(false)}
          onInvited={reload}
          tenantId={tenantId}
        />
      ) : null}

      {pendingRevoke !== null ? (
        <ConfirmDialog
          confirmLabel="Revoke access"
          consequence={
            <>
              Remove {pendingRevoke.fanatic_email ?? pendingRevoke.invite_email ?? 'this account'}{' '}
              from this team? They will no longer see stats or video, and this cannot be undone —
              they will not be able to follow or be re-invited.
            </>
          }
          error={revokeError}
          onCancel={() => {
            setPendingRevoke(null);
            setRevokeError(null);
          }}
          onConfirm={() => revoke(pendingRevoke)}
          title="Revoke fanatic access?"
          tone="danger"
        >
          <TextField
            hint="Optional. Recorded in the audit trail."
            label="Reason"
            maxLength={MAX_REASON_LENGTH}
            name="revoke-reason"
            onChange={(event) => setRevokeReason(event.target.value)}
            value={revokeReason}
          />
        </ConfirmDialog>
      ) : null}
    </section>
  );
}

// ------------------------------------------------------------------- invites

function InviteFanaticDialog({
  tenantId,
  onInvited,
  onClose,
}: {
  tenantId: string;
  onInvited: () => void;
  onClose: () => void;
}): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [inviteLink, setInviteLink] = useState<{ email: string; url: string } | null>(null);

  const submit = async (): Promise<void> => {
    const trimmed = email.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setFieldError('Enter a valid email address, for example fan@example.com.');
      return;
    }
    setIsBusy(true);
    setFieldError(null);
    setError(null);
    try {
      const sent = await apiClient.sendFanaticInvite(tenantId, { email: trimmed });
      setInviteLink({ email: trimmed, url: sent.invite_link });
      setEmail('');
      showToast(`Invite sent to ${trimmed}.`, 'success');
      onInvited();
    } catch (caught) {
      setError(toMessage(caught, 'We could not send that invite. Please try again.'));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <OverlayDialog title="Invite a fanatic" onClose={onClose}>
      <form
        className="stack fan-invite"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <p className="muted fan-invite__note">
          Works in either access mode. The recipient accepts from the emailed link; until then the
          invite shows as “Invite pending” in the list.
        </p>
        {error !== null ? (
          <p className="banner banner--error" role="alert">
            {error}
          </p>
        ) : null}
        <TextField
          autoComplete="email"
          error={fieldError}
          label="Email address"
          name="fanatic-invite-email"
          onChange={(event) => setEmail(event.target.value)}
          type="email"
          value={email}
        />
        {inviteLink !== null ? (
          <div className="fan-invite-link" role="status">
            <span>
              Invite sent to <strong>{inviteLink.email}</strong>. Copy this one-time link if you
              want to send it yourself:
            </span>
            <span className="fan-invite-link__url">{inviteLink.url}</span>
          </div>
        ) : null}
        <div className="btn-row dialog-actions">
          <button className="btn btn--secondary" onClick={onClose} type="button">
            {inviteLink === null ? 'Cancel' : 'Done'}
          </button>
          <button className="btn btn--primary" disabled={isBusy} type="submit">
            {isBusy ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </form>
    </OverlayDialog>
  );
}

// ----------------------------------------------------------------- audit view

function GrantAuditLog({ grants }: { grants: readonly TeamFanaticGrant[] }): JSX.Element {
  const rows = useMemo(
    () =>
      [...grants]
        .sort((left, right) => right.updated_at.localeCompare(left.updated_at))
        .map((grant) => ({
          id: grant.grant_id,
          email: grant.fanatic_email ?? grant.invite_email ?? 'Unknown account',
          action:
            grant.status === 'revoked'
              ? 'fanatic.grant.revoked'
              : grant.status === 'pending_invite'
                ? 'fanatic.invite.sent'
                : grant.granted_by === null
                  ? 'fanatic.grant.self_follow'
                  : 'fanatic.invite.accepted',
          actor:
            grant.status === 'revoked'
              ? (grant.revoked_by ?? 'team admin')
              : (grant.granted_by ?? 'the fanatic'),
          moment: grant.revoked_at ?? grant.accepted_at ?? grant.granted_at ?? grant.created_at,
          reason: grant.revoked_reason,
        })),
    [grants]
  );

  return (
    <DeskSection
      className="fan-history"
      title="Grant history"
      description="Most recent first, for the rows listed above. The signed audit log keeps the full record."
    >
      {rows.length === 0 ? (
        <p className="muted fan-history__empty">Nothing to show yet.</p>
      ) : (
        <ul className="fan-history__list">
          {rows.map((row) => (
            <li className="fan-history__row" key={row.id}>
              <span className="fan-history__email" title={row.email}>
                {row.email}
              </span>
              <span className="mono fan-history__action">{row.action}</span>
              <span
                className="fan-history__meta"
                title={`${row.actor} · ${formatMoment(row.moment)}`}
              >
                {row.actor} · {formatMoment(row.moment)}
                {row.reason === null ? '' : ` · “${row.reason}”`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </DeskSection>
  );
}
